<template>
  <v-navigation-drawer
    v-model="isCartOpen"
    location="right"
    :width="cartWidth"
    class="cart-drawer"
    :temporary="isMobile"
    :permanent="!isMobile"
    :rail="!isCartOpen && !isMobile"
    :rail-width="0"
    :scrim="isMobile"
    aria-label="Shopping cart"
    role="region"
  >
    <div class="cart-header pa-3">
      <div class="d-flex align-center justify-space-between">
        <h2 class="cart-heading d-flex align-center">
          <v-icon class="mr-2" size="large" aria-hidden="true"
            >fas fa-shopping-cart</v-icon
          >
          <span>Shopping cart</span>
        </h2>
        <v-btn
          icon
          size="small"
          variant="text"
          @click="closeCart"
          aria-label="Close shopping cart"
        >
          <v-icon size="small" aria-hidden="true">fas fa-times</v-icon>
        </v-btn>
      </div>
    </div>
    <v-divider></v-divider>
    <purchase-price-summary
      :purchase="props.purchase"
      :is-editable="true"
      :hide-empty-payments-section="true"
      :forceMobileView="true"
      :service="props.service"
      @remove-reserved-seat-ticket="handleRemoveReservedSeatTicket"
    />

    <div class="cart-footer" role="banner">
      <v-divider></v-divider>
      <div class="pa-3">
        <v-btn
          v-if="!hideProceedToCheckout"
          block
          color="primary"
          size="large"
          @click="handleProceedToCheckout"
          :disabled="!props.purchase.tickets?.length"
          aria-label="Proceed to checkout"
        >
          Proceed to Checkout
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  PublicPurchaseServiceViewModel,
  TicketPurchaseDtoViewModel,
} from "@/viewmodels.g";
import PurchasePriceSummary from "@common/purchase/purchase-price-summary.vue";
import { TicketPurchaseTicket } from "app-public/models.g";
import { PurchaseSeatMapContext } from "@common/seat-map-context";
import { useDisplay } from "vuetify";

const viewportWidth = ref(window.innerWidth);

const updateViewportWidth = () => {
  viewportWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener("resize", updateViewportWidth);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateViewportWidth);
});

const display = useDisplay();
const isMobile = computed(() => display.smAndDown.value);

const cartWidth = computed(() => {
  if (viewportWidth.value < 1920) return 380;
  return 500;
});
const props = defineProps<{
  purchase: TicketPurchaseDtoViewModel;
  modelValue: boolean;
  context?: PurchaseSeatMapContext;
  service: PublicPurchaseServiceViewModel;
  hideProceedToCheckout?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "proceedToCheckout"): void;
}>();

const isCartOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

function handleProceedToCheckout() {
  emit("proceedToCheckout");
  isCartOpen.value = false;
}

function closeCart() {
  isCartOpen.value = false;
}

async function handleRemoveReservedSeatTicket(ticket: TicketPurchaseTicket) {
  await props.context!.removeReservedSeatTicketFromCart(ticket);
}
</script>

<style lang="scss" scoped>
.cart-drawer {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  // Desktop: Vuetify handles position:absolute via :absolute prop.
  // Anchored to nearest positioned ancestor (.purchase-wrapper).
  @media (min-width: 960px) {
    z-index: 11;
  }

  // Mobile: fixed overlay
  @media (max-width: 959px) {
    z-index: 100000;
  }

  .cart-header {
    background-color: #f5f5f5;
    position: sticky;
    top: 0;
    z-index: 1;

    .cart-heading {
      font-size: 1.25rem;
      font-weight: 500;
      margin: 0;
      line-height: 1.5;
    }
  }
}
</style>
